"use client";

import {
  ArrowRight,
  Bot,
  Check,
  ClipboardCheck,
  FileClock,
  KeyRound,
  RadioTower,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { TenantMembershipSummary, TenantProductScope } from "@/core/auth/current-tenant";
import {
  buildOnboardingPlan,
  resolvePrimaryOnboardingProduct,
  type LogisticsRoleFocus,
  type OnboardingPlan,
} from "@/core/onboarding/product-onboarding";
import type { OnboardingProgressState, OnboardingStepId, OnboardingStatus } from "@/core/onboarding/progress";

type PersistedOnboardingProgress = {
  primaryProduct: TenantProductScope;
  roleFocus?: LogisticsRoleFocus;
  status: OnboardingStatus;
  currentStep: OnboardingStepId;
  completedStepIds: OnboardingStepId[];
  completedAt?: number;
} | null;

const currentOnboarding = makeFunctionReference<
  "query",
  { tenantId: string },
  PersistedOnboardingProgress
>("onboarding:current");

const selectOnboardingRole = makeFunctionReference<
  "mutation",
  { tenantId: string; primaryProduct: TenantProductScope; roleFocus: LogisticsRoleFocus },
  OnboardingProgressState
>("onboarding:selectRole");

const completeOnboarding = makeFunctionReference<
  "mutation",
  { tenantId: string; primaryProduct: TenantProductScope },
  OnboardingProgressState
>("onboarding:complete");

const skipOnboarding = makeFunctionReference<
  "mutation",
  { tenantId: string; primaryProduct: TenantProductScope },
  OnboardingProgressState
>("onboarding:skip");

type OnboardingGateProps = {
  tenant: TenantMembershipSummary;
  children: ReactNode;
};

export function OnboardingGate({ tenant, children }: OnboardingGateProps) {
  const progress = useQuery(currentOnboarding, { tenantId: tenant.tenantId });
  const primaryProduct = resolvePrimaryOnboardingProduct(tenant.products);
  const [dismissed, setDismissed] = useState(false);

  if (progress === undefined) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <QuerySkeleton surface="tenantWorkspace" />
      </main>
    );
  }

  if (dismissed || progress?.status === "completed" || progress?.status === "skipped") {
    return <>{children}</>;
  }

  return (
    <HaulbaseOnboardingFlow
      tenant={tenant}
      primaryProduct={progress?.primaryProduct ?? primaryProduct}
      initialRoleFocus={progress?.roleFocus}
      onDismiss={() => setDismissed(true)}
    />
  );
}

function HaulbaseOnboardingFlow({
  tenant,
  primaryProduct,
  initialRoleFocus,
  onDismiss,
}: {
  tenant: TenantMembershipSummary;
  primaryProduct: TenantProductScope;
  initialRoleFocus?: LogisticsRoleFocus;
  onDismiss: () => void;
}) {
  const selectRole = useMutation(selectOnboardingRole);
  const complete = useMutation(completeOnboarding);
  const skip = useMutation(skipOnboarding);
  const [stepIndex, setStepIndex] = useState(initialRoleFocus ? 5 : 0);
  const [roleFocus, setRoleFocus] = useState<LogisticsRoleFocus | undefined>(initialRoleFocus);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const plan = useMemo(
    () =>
      buildOnboardingPlan({
        tenantName: tenant.tenantName,
        products: tenant.products,
        roleFocus: roleFocus ?? "dispatcher",
      }),
    [roleFocus, tenant.products, tenant.tenantName],
  );
  const steps = ["Welcome", "Scope", "Agent", "Abilities", "Role", "First action", "Setup"];
  const currentStep = steps[stepIndex] ?? "Welcome";

  function advance() {
    setError(null);
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  }

  async function persistRole(nextRole: LogisticsRoleFocus) {
    setBusy(true);
    setError(null);
    try {
      await selectRole({ tenantId: tenant.tenantId, primaryProduct, roleFocus: nextRole });
      setRoleFocus(nextRole);
      setStepIndex(5);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save onboarding role");
    } finally {
      setBusy(false);
    }
  }

  async function finish(actionHref?: string) {
    setBusy(true);
    setError(null);
    try {
      await complete({ tenantId: tenant.tenantId, primaryProduct });
      onDismiss();
      if (actionHref) window.location.assign(actionHref);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not complete onboarding");
      setBusy(false);
    }
  }

  async function skipFlow() {
    setBusy(true);
    setError(null);
    try {
      await skip({ tenantId: tenant.tenantId, primaryProduct });
      onDismiss();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not skip onboarding");
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-background">
      <section className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Bot size={20} />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">{currentStep}</div>
              <h1 className="text-xl font-semibold tracking-tight">Haulbase onboarding</h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {steps.map((step, index) => (
              <span
                key={step}
                className={`h-1.5 w-8 rounded-full ${index <= stepIndex ? "bg-primary" : "bg-muted"}`}
                aria-label={`${step} ${index <= stepIndex ? "active" : "pending"}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-2">
          {steps.map((step, index) => (
            <button
              key={step}
              type="button"
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
                index === stepIndex ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
              onClick={() => setStepIndex(index)}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-xs">
                {index < stepIndex ? <Check size={12} /> : index + 1}
              </span>
              {step}
            </button>
          ))}
          <button
            type="button"
            className="mt-4 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={skipFlow}
            disabled={busy}
          >
            Skip for now
          </button>
        </aside>

        <section className="min-h-[560px] rounded-md border border-border bg-card p-5">
          {stepIndex === 0 ? <WelcomeStep plan={plan} onNext={advance} /> : null}
          {stepIndex === 1 ? <ScopeStep plan={plan} onNext={advance} /> : null}
          {stepIndex === 2 ? <AgentStep plan={plan} onNext={advance} /> : null}
          {stepIndex === 3 ? <AbilitiesStep plan={plan} onNext={advance} /> : null}
          {stepIndex === 4 ? (
            <RoleStep selectedRole={roleFocus} busy={busy} onSelect={persistRole} />
          ) : null}
          {stepIndex === 5 ? (
            <FirstActionStep plan={plan} busy={busy} onComplete={finish} onNext={advance} />
          ) : null}
          {stepIndex === 6 ? <SetupStep plan={plan} busy={busy} onComplete={() => finish()} /> : null}
          {error ? (
            <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}
        </section>
      </section>
    </main>
  );
}

function WelcomeStep({ plan, onNext }: { plan: OnboardingPlan; onNext: () => void }) {
  return (
    <div className="flex h-full flex-col justify-between gap-8">
      <div className="space-y-5">
        <div className="inline-flex h-10 items-center gap-2 rounded-md border border-border bg-muted px-3 text-sm font-medium">
          <Sparkles size={16} />
          {plan.productLabel}
        </div>
        <div className="max-w-2xl space-y-3">
          <h2 className="text-3xl font-semibold tracking-tight">{plan.welcomeTitle}</h2>
          <p className="text-base leading-7 text-muted-foreground">
            This short setup introduces the Haulbase Agent, the product scopes active in this workspace,
            and the freight actions that stay behind approval gates.
          </p>
        </div>
      </div>
      <Button className="w-fit" onClick={onNext}>
        Start <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function ScopeStep({ plan, onNext }: { plan: OnboardingPlan; onNext: () => void }) {
  return (
    <div className="space-y-6">
      <StepHeader
        icon={<KeyRound size={18} />}
        title="Your enabled product scopes"
        body="Haulbase only exposes the operational surfaces this tenant is allowed to use."
      />
      <div className="grid gap-3 md:grid-cols-3">
        {plan.scopeCards.map((scope) => (
          <div key={scope.product} className="rounded-md border border-border bg-background p-4">
            <div className="mb-3 text-xs font-medium uppercase text-muted-foreground">{scope.mode}</div>
            <h3 className="text-base font-semibold">{scope.label}</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{scope.summary}</p>
          </div>
        ))}
      </div>
      <Button onClick={onNext}>
        Continue <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function AgentStep({ plan, onNext }: { plan: OnboardingPlan; onNext: () => void }) {
  return (
    <div className="space-y-6">
      <StepHeader icon={<Bot size={18} />} title={plan.agentIntro.title} body={plan.agentIntro.body} />
      <div className="grid gap-3">
        {plan.agentIntro.capabilities.map((capability) => (
          <div key={capability} className="flex gap-3 rounded-md border border-border bg-background p-4">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
              <Check size={14} />
            </div>
            <p className="text-sm leading-6">{capability}</p>
          </div>
        ))}
      </div>
      <Button onClick={onNext}>
        Continue <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function AbilitiesStep({ plan, onNext }: { plan: OnboardingPlan; onNext: () => void }) {
  return (
    <div className="space-y-6">
      <StepHeader
        icon={<ShieldCheck size={18} />}
        title="What the agent can touch"
        body="The current pilot posture is useful by default and conservative around freight commitments."
      />
      <div className="grid gap-3 lg:grid-cols-3">
        <AbilityColumn title="Can do" icon={<RadioTower size={16} />} items={plan.abilityMatrix.canDo} />
        <AbilityColumn title="Needs approval" icon={<ClipboardCheck size={16} />} items={plan.abilityMatrix.needsApproval} />
        <AbilityColumn title="Cannot do" icon={<ShieldCheck size={16} />} items={plan.abilityMatrix.cannotDo} />
      </div>
      <Button onClick={onNext}>
        Continue <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function RoleStep({
  selectedRole,
  busy,
  onSelect,
}: {
  selectedRole?: LogisticsRoleFocus;
  busy: boolean;
  onSelect: (role: LogisticsRoleFocus) => void;
}) {
  const roles: Array<{ id: LogisticsRoleFocus; label: string; body: string }> = [
    { id: "dispatcher", label: "Dispatcher", body: "Focus on active loads, tracking, appointments, and exceptions." },
    { id: "broker", label: "Broker or carrier sales", body: "Focus on coverage, carrier fit, outreach drafts, and load-board work." },
    { id: "ops_manager", label: "Operations manager", body: "Focus on backlog, approvals, risk, and team throughput." },
    { id: "finance", label: "Finance or billing", body: "Focus on invoices, settlements, holds, claims, and money-impacting approvals." },
    { id: "admin_owner", label: "Admin or owner", body: "Focus on users, product scopes, integrations, approvals, and production gates." },
  ];

  return (
    <div className="space-y-6">
      <StepHeader
        icon={<Truck size={18} />}
        title="Choose your logistics role"
        body="Haulbase uses this to put the right first workflow in front of you."
      />
      <div className="grid gap-3 md:grid-cols-2">
        {roles.map((role) => (
          <button
            key={role.id}
            type="button"
            disabled={busy}
            className={`rounded-md border p-4 text-left transition-colors ${
              selectedRole === role.id ? "border-primary bg-muted" : "border-border bg-background hover:bg-muted"
            }`}
            onClick={() => onSelect(role.id)}
          >
            <div className="font-semibold">{role.label}</div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{role.body}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function FirstActionStep({
  plan,
  busy,
  onComplete,
  onNext,
}: {
  plan: OnboardingPlan;
  busy: boolean;
  onComplete: (href?: string) => void;
  onNext: () => void;
}) {
  const firstAction = plan.firstActions[0];

  return (
    <div className="space-y-6">
      <StepHeader
        icon={<FileClock size={18} />}
        title="Start with one useful workflow"
        body="The first action is scoped to your role and keeps freight commitments guarded."
      />
      <div className="rounded-md border border-border bg-background p-5">
        <div className="text-xs font-medium uppercase text-muted-foreground">Recommended first action</div>
        <h3 className="mt-2 text-xl font-semibold tracking-tight">{firstAction.title}</h3>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{firstAction.description}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => onComplete(firstAction.href)}>
            Open workflow <ArrowRight size={16} />
          </Button>
          <Button variant="secondary" disabled={busy} onClick={onNext}>
            Review setup
          </Button>
        </div>
      </div>
    </div>
  );
}

function SetupStep({
  plan,
  busy,
  onComplete,
}: {
  plan: OnboardingPlan;
  busy: boolean;
  onComplete: () => void;
}) {
  return (
    <div className="space-y-6">
      <StepHeader
        icon={<ClipboardCheck size={18} />}
        title="Pilot setup checklist"
        body="These are the operational gates that turn the workspace from a demo into a controlled pilot."
      />
      <div className="grid gap-3">
        {plan.setupChecklist.map((item) => (
          <div key={item} className="flex gap-3 rounded-md border border-border bg-background p-4">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
              <Check size={14} />
            </div>
            <p className="text-sm leading-6">{item}</p>
          </div>
        ))}
      </div>
      <Button disabled={busy} onClick={onComplete}>
        Enter workspace <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function StepHeader({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="flex max-w-3xl gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </div>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function AbilityColumn({ title, icon, items }: { title: string; icon: ReactNode; items: string[] }) {
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item} className="text-sm leading-6 text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
